import React, { useState, useMemo, useRef } from 'react';
import './ApiDocs.css';

const BASE = 'https://colonny.xyz';

const SECTIONS = [
  {
    id: 'auth', icon: '🔐', label: 'Autenticación', color: '#7C3AED',
    description: 'Login y validación de credenciales. La sesión viaja en una cookie httpOnly (colonny-access-token) que el navegador maneja solo — no hace falta mandar ningún header manual en requests autenticados.',
    apis: [
      { method: 'POST', path: '/api/login', desc: 'Iniciar sesión. Retorna usuario y espacio activo.', auth: false, body: { nick: 'string', password: 'string' }, response: { success: true, user: { id: 1, nick: 'beeto', role: 'admin' }, currentSpace: { id: 'uuid', name: 'Mi Espacio' } } },
      { method: 'POST', path: '/api/validate-nick', desc: 'Verificar si un nick está disponible.', auth: true, body: { nick: 'string' }, response: { available: true } },
      { method: 'POST', path: '/api/validate-email', desc: 'Verificar si un email ya está registrado.', auth: false, body: { email: 'string' }, response: { available: true } }
    ]
  },
  {
    id: 'users', icon: '👤', label: 'Usuarios', color: '#0EA5E9',
    description: 'Gestión de perfiles de usuario. Cada usuario tiene su propio perfil, rol y acceso a espacios.',
    apis: [
      { method: 'GET', path: '/api/users', desc: 'Obtener lista de todos los usuarios.', auth: true, response: [{ id: 1, nick: 'beeto', email: 'email@colonny.xyz', role: 'admin' }] },
      { method: 'GET', path: '/api/users/:id', desc: 'Obtener perfil de un usuario específico.', auth: true, params: { id: 'ID numérico del usuario' }, response: { id: 1, nick: 'beeto', role: 'admin' } },
      { method: 'PUT', path: '/api/users/:id', desc: 'Actualizar perfil (nick, email, avatar, contraseña, rol).', auth: true, body: { nick: 'nuevo_nick', email: 'nuevo@email.com', role: 'admin' }, response: { success: true } },
      { method: 'GET', path: '/api/users/:id/contributions', desc: 'Historial de contribuciones del usuario.', auth: true, response: [{ date: '2026-05-01', count: 12 }] }
    ]
  },
  {
    id: 'permissions', icon: '🛡️', label: 'Sistema de Permisos (Roles)', color: '#F43F5E',
    description: 'Gestión de roles de sistema (admin, agent, broker, collab) con autoguardado (Optimistic UI) mediante peticiones PUT a usuarios.',
    apis: [
      { method: 'PUT', path: '/api/users/:id', desc: 'Actualizar el rol (y otros datos). El frontend usa Optimistic UI para mover los tags instantáneamente y hacer la petición en background. Si el usuario logueado no es admin/sub-admin, revierte visualmente la acción.', auth: true, body: { role: 'admin' }, response: { success: true } },
      { method: 'INTERNAL', path: 'save-pulse (useSaveStatusStore)', desc: 'El interceptor global de window.fetch (app/savePulse.js) registra cada PUT/POST/DELETE en useSaveStatusStore; el indicador en Menu2 parpadea en verde o rojo según el status HTTP.', auth: false, response: null }
    ]
  },
  {
    id: 'docs', icon: '📄', label: 'Documentos', color: '#10B981',
    description: 'Sistema jerárquico de documentos tipo Notion con permisos granulares por usuario.',
    apis: [
      { method: 'GET', path: '/api/docs', desc: 'Obtener árbol de documentos accesibles para el usuario.', auth: true, response: [{ id: 'uuid', title: 'Mi Doc', parent_id: null, icon: '📁' }] },
      { method: 'GET', path: '/api/docs/:id', desc: 'Obtener documento específico con contenido y permisos.', auth: true, response: { id: 'uuid', title: 'Mi Doc', content: '...', permissions: [] } },
      { method: 'POST', path: '/api/docs', desc: 'Crear nuevo documento.', auth: true, body: { title: 'Nuevo Doc', parent_id: null, icon: '📄', owner_id: 1 }, response: { success: true, id: 'uuid' } },
      { method: 'PUT', path: '/api/docs/:id', desc: 'Actualizar documento (título, contenido, icono, permisos).', auth: true, body: { title: 'Actualizado', content: '<p>...</p>' }, response: { success: true } },
      { method: 'DELETE', path: '/api/docs/:id', desc: 'Eliminar documento y todos sus hijos.', auth: true, response: { success: true } }
    ]
  },
  {
    id: 'databases', icon: '🗄️', label: 'Bases de Datos', color: '#F59E0B',
    description: 'Tablas dinámicas con columnas personalizadas (texto, número, fecha, tags, select, usuario).',
    apis: [
      { method: 'GET', path: '/api/dataindex/search?q=', desc: 'Buscar bases de datos por nombre.', auth: true, response: [{ id: 'uuid', name: 'Mi Tabla' }] },
      { method: 'POST', path: '/api/dataindex', desc: 'Crear nueva base de datos/tabla.', auth: true, body: { name: 'Clientes', columns: [{ name: 'Nombre', type: 'text' }] }, response: { success: true, id: 'uuid' } },
      { method: 'GET', path: '/api/dataindex/:id/rows', desc: 'Obtener todas las filas de una tabla.', auth: true, response: { rows: [{ id: 'uuid', data: {} }], columns: [] } },
      { method: 'POST', path: '/api/dataindex/:id/rows', desc: 'Agregar nueva fila a la tabla.', auth: true, body: { data: { Nombre: 'Cliente A' } }, response: { success: true, row: { id: 'uuid' } } },
      { method: 'PATCH', path: '/api/dataindex/:id/rows/:rowId', desc: 'Editar celda específica de una fila.', auth: true, body: { column: 'Estado', value: 'completado' }, response: { success: true } },
      { method: 'DELETE', path: '/api/dataindex/:id/rows/:rowId', desc: 'Eliminar una fila.', auth: true, response: { success: true } },
      { method: 'POST', path: '/api/dataindex/:id/columns', desc: 'Agregar nueva columna a la tabla.', auth: true, body: { name: 'Fecha', type: 'date' }, response: { success: true } },
      { method: 'PATCH', path: '/api/dataindex/:id', desc: 'Actualizar metadatos de la tabla.', auth: true, body: { name: 'Nuevo nombre' }, response: { success: true } },
      { method: 'DELETE', path: '/api/dataindex/:uid', desc: 'Eliminar tabla completa.', auth: true, response: { success: true } }
    ]
  },
  {
    id: 'arch-api-tasks', icon: '📝', label: 'Tareas (Tasks)', color: '#3B82F6',
    description: 'Gestión de tareas colaborativas con asignaciones, temporizadores y cálculos de horario automático. Soporta Listener en vivo.',
    apis: [
      { method: 'POST', path: '/api/tasks', desc: 'Crear nueva tarea. Si se envía sin start_time, intenta Auto-Schedule (findNextAvailableSlot).', auth: true, body: { title: 'Revisión', duration_cols: 2, assignee: 'beeto' }, response: { success: true, task: { id: 1, event_id: 'uuid' } } },
      { method: 'PUT', path: '/api/tasks/:id', desc: 'Actualizar tarea. Manda autoSchedule: true para recalcular horario.', auth: true, body: { stage: 'check', autoSchedule: true, duration_minutes: 60 }, response: { success: true } },
      { method: 'DELETE', path: '/api/tasks/:id', desc: 'Eliminar tarea (soft delete).', auth: true, response: { success: true } }
    ]
  },
  {
    id: 'listener-console', icon: '📡', label: 'Listener Console', color: '#10B981',
    description: 'Consola de depuración en vivo flotante para monitorear tráfico de red y WebSockets. \n\nArquitectura:\n- Monkey-Patching de Fetch: El componente sobrescribe globalmente window.fetch para atrapar salidas y entradas de red.\n- Conexión Socket: Inicializa su propio cliente io() para atrapar broadcast.\n- Auto-minimizado: 1.5s después de atrapar el primer dato, se minimiza a un tag inferior.\n- Botón Copiar: Permite exportar JSON devuelto para depuración.',
    apis: [
      { method: 'EVENT', path: 'API OUT', desc: 'Captura y formatea peticiones Fetch (ej. PUT /api/tasks) salientes, incluyendo body enviado.', auth: false, response: null },
      { method: 'EVENT', path: 'API IN / ERR', desc: 'Intercepta la respuesta de Fetch. Clona el stream de respuesta para leer y mostrar el JSON crudo en pantalla.', auth: false, response: { success: true, task: { id: 1 } } },
      { method: 'WS', path: 'task_created', desc: 'Atrapado por el listener cuando el servidor emite una nueva tarea a todos.', auth: false, response: null },
      { method: 'WS', path: 'task_updated', desc: 'Atrapado por el listener al haber un cambio de estado u horario en cualquier cliente.', auth: false, response: null }
    ]
  },
  {
    id: 'comments', icon: '💬', label: 'Comentarios', color: '#EC4899',
    description: 'Sistema de comentarios por documento con reacciones emoji y soft delete.',
    apis: [
      { method: 'GET', path: '/api/comments/:docId', desc: 'Obtener comentarios de un documento.', auth: true, response: [{ id: 1, content: 'Buen trabajo!', author: 'beeto', reactions: {} }] },
      { method: 'POST', path: '/api/comments', desc: 'Publicar nuevo comentario en un documento.', auth: true, body: { document_id: 'uuid', author_id: 1, content: 'Mi comentario', files: [] }, response: { success: true, comment: { id: 1 } } },
      { method: 'POST', path: '/api/comments/update', desc: 'Editar contenido de un comentario existente.', auth: true, body: { commentId: 1, content: 'Editado' }, response: { success: true } },
      { method: 'POST', path: '/api/comments/reaction', desc: 'Agregar o quitar reacción emoji a un comentario.', auth: true, body: { commentId: 1, userId: 1, emoji: '❤️' }, response: { success: true } },
      { method: 'DELETE', path: '/api/comments/:commentId', desc: 'Eliminar comentario (soft delete).', auth: true, response: { success: true } }
    ]
  },
  {
    id: 'notifications', icon: '🔔', label: 'Notificaciones', color: '#EF4444',
    description: 'Notificaciones por usuario con estado de leído individual.',
    apis: [
      { method: 'GET', path: '/api/notifications/:userId', desc: 'Obtener notificaciones del usuario (últimas 200).', auth: true, response: [{ id: 1, type: 'comment', read: false, created_at: '2026-05-01' }] },
      { method: 'POST', path: '/api/notifications/mark-read', desc: 'Marcar notificación como leída para un usuario.', auth: true, body: { notificationId: 1, userId: 1 }, response: { success: true } },
      { method: 'POST', path: '/api/notifications/create', desc: 'Crear notificación para uno o varios usuarios.', auth: true, body: { users: [{ userId: 1, read: false }], user_actor: 2, type: 'comment', metadata: {}, link: '/docs/uuid' }, response: { success: true } }
    ]
  },
  {
    id: 'recordings', icon: '🎥', label: 'Grabaciones', color: '#6366F1',
    description: 'Grabaciones de pantalla y video. Subida a GCS, metadatos en base de datos.',
    apis: [
      { method: 'GET', path: '/api/recordings', desc: 'Listar grabaciones del usuario.', auth: true, response: [{ id: 1, name: 'sesion.webm', url: 'https://...', created_at: '2026-05-01' }] },
      { method: 'POST', path: '/api/recordings/upload', desc: 'Subir grabación (multipart/form-data). Campo: file.', auth: true, body: { file: '<binary>', userId: '1', name: 'mi_sesion' }, response: { success: true, url: 'https://storage.googleapis.com/...' } },
      { method: 'PUT', path: '/api/recordings/:id', desc: 'Actualizar metadatos de grabación.', auth: true, body: { name: 'nuevo nombre' }, response: { success: true } },
      { method: 'DELETE', path: '/api/recordings/:id', desc: 'Eliminar grabación.', auth: true, response: { success: true } }
    ]
  },
  {
    id: 'media', icon: '🖼️', label: 'Archivos y Media', color: '#14B8A6',
    description: 'Subida y gestión de archivos (imágenes, PDFs, etc.) en Google Cloud Storage.',
    apis: [
      { method: 'POST', path: '/api/upload/avatar', desc: 'Subir avatar (profile | img-avatar) — único flujo que pasa por el backend, redimensiona con sharp antes de subir a GCS.', auth: true, body: { file: '<binary>', type: 'profile' }, response: { success: true, url: 'https://storage.googleapis.com/...' } },
      { method: 'POST', path: '/api/upload/request', desc: 'Pide una signed POST policy de GCS para subir el resto de los tipos directo al bucket (el archivo no pasa por este backend). type: decoration | document | comment_embed | doc_embed | task_attachment | client_document | dataindex_file | etc.', auth: true, body: { type: 'decoration', filename: 'foto.png', contentType: 'image/png' }, response: { success: true, url: 'https://storage.googleapis.com/...', fields: {}, path: 'public/decorations/...' } },
      { method: 'POST', path: '/api/upload/confirm', desc: 'Confirma una subida directa a GCS ya completada (paso 2 del flujo de /request) — verifica el objeto contra el bucket y crea la fila en `media`/`decoration`.', auth: true, body: { path: 'public/decorations/...', type: 'decoration', name: 'foto.png', size: 12345, contentType: 'image/png' }, response: { success: true, url: 'https://storage.googleapis.com/...' } },
      { method: 'GET', path: '/api/media/decorations', desc: 'Obtener decoraciones/stickers disponibles.', auth: true, response: [{ id: 1, name: 'sticker1', url: 'https://...' }] },
      { method: 'DELETE', path: '/api/media/decorations/:id', desc: 'Eliminar decoración.', auth: true, response: { success: true } }
    ]
  },
  {
    id: 'spaces', icon: '🏢', label: 'Espacios (Workspaces)', color: '#F97316',
    description: 'Workspaces multi-tenant. Cada usuario pertenece a espacios con roles admin/member.',
    apis: [
      { method: 'GET', path: '/api/space/:id', desc: 'Obtener información de un espacio.', auth: true, response: { id: 'uuid', space_name: 'Mi Empresa', role: 'admin' } },
      { method: 'PUT', path: '/api/space/:id', desc: 'Actualizar espacio (nombre, icono, configuración).', auth: true, body: { space_name: 'Nuevo Nombre' }, response: { success: true } },
      { method: 'GET', path: '/api/space/:id/members', desc: 'Listar miembros del espacio.', auth: true, response: [{ id: 1, nick: 'beeto', role: 'admin' }] },
      { method: 'POST', path: '/api/space/:id/invite', desc: 'Invitar usuario al espacio por email.', auth: true, body: { email: 'user@colonny.xyz', role: 'member' }, response: { success: true } },
      { method: 'PUT', path: '/api/space/:id/members/:userId/role', desc: 'Cambiar rol de un miembro.', auth: true, body: { role: 'admin' }, response: { success: true } },
      { method: 'DELETE', path: '/api/space/:id/members/:userId', desc: 'Remover miembro del espacio.', auth: true, response: { success: true } }
    ]
  },
  {
    id: 'calendar', icon: '📅', label: 'Calendario', color: '#8B5CF6',
    description: 'Calendarios colaborativos y eventos con sincronización Google Calendar.',
    apis: [
      { method: 'GET', path: '/api/calendars', desc: 'Listar calendarios del usuario.', auth: true, response: [{ id: 'uuid', name: 'Personal', color: '#7C3AED' }] },
      { method: 'POST', path: '/api/calendars', desc: 'Crear nuevo calendario.', auth: true, body: { name: 'Trabajo', color: '#10B981', userId: 1 }, response: { success: true, id: 'uuid' } },
      { method: 'GET', path: '/api/events', desc: 'Obtener eventos (query: start, end, userId, userNick, onlyMine). Si no se manda start/end, usa el día de hoy por defecto. Si onlyMine no es "false" y hay userId, filtra a eventos propios (dueño del calendario, creador, tarea asignada o invitado).', auth: true, response: [{ id: 'uuid', title: 'Reunión', start: '2026-05-01T10:00:00' }] },
      { method: 'POST', path: '/api/events', desc: 'Crear evento en calendario.', auth: true, body: { title: 'Reunión', start: '2026-05-01T10:00:00', end: '2026-05-01T11:00:00', calendarId: 'uuid' }, response: { success: true, id: 'uuid' } },
      { method: 'PUT', path: '/api/events/:id', desc: 'Actualizar evento.', auth: true, body: { title: 'Reunión actualizada' }, response: { success: true } },
      { method: 'DELETE', path: '/api/events/:id', desc: 'Eliminar evento.', auth: true, response: { success: true } }
    ]
  },
  {
    id: 'payments', icon: '💳', label: 'Pagos', color: '#059669',
    description: 'Links de pago y procesamiento de cobros.',
    apis: [
      { method: 'GET', path: '/api/payments/links', desc: 'Listar links de pago del usuario.', auth: true, response: [{ id: 'uuid', amount: 100, currency: 'MXN', status: 'active' }] },
      { method: 'POST', path: '/api/payments/links', desc: 'Crear link de pago.', auth: true, body: { amount: 100, currency: 'MXN', description: 'Servicio', userId: 1 }, response: { success: true, url: 'https://colonny.xyz/pay/uuid' } },
      { method: 'POST', path: '/api/payments/charge', desc: 'Procesar cobro en un link de pago.', auth: false, body: { linkId: 'uuid', cardToken: 'tok_...', email: 'cliente@email.com' }, response: { success: true } }
    ]
  },
  {
    id: 'realtime', icon: '⚡', label: 'Tiempo Real (Socket.IO)', color: '#DC2626',
    description: 'WebSocket para sincronización en tiempo real entre usuarios activos.',
    apis: [
      { method: 'WS', path: '/socket.io', desc: 'Conectar al servidor Socket.IO.', auth: false, body: { query: { userId: '1' } }, response: null },
      { method: 'EVENT', path: 'doc_updated', desc: 'Emitido cuando un documento es actualizado por otro usuario.', auth: false, response: { id: 'uuid', title: 'Doc actualizado' } },
      { method: 'EVENT', path: 'player_update', desc: 'Estado y posición de usuarios activos en el mapa.', auth: false, response: { userId: 1, x: 120, y: 340 } }
    ]
  },
  {
    id: 'characters', label: 'Personajes', icon: '🎭', color: '#A855F7',
    description: 'Avatares personalizados compartidos entre todos los usuarios. Cada usuario puede crear y publicar su propio personaje para el mapa interactivo.',
    apis: [
      {
        method: 'GET', path: '/api/characters', auth: false,
        desc: 'Obtener todos los personajes públicos disponibles para seleccionar en el mapa.',
        response: { success: true, characters: [{ id: 1, name: 'Mi Personaje', description: 'Oficinista', frames: {}, preview_url: 'https://...', created_by: 1, creator_nick: 'beeto', created_at: '2026-05-03T17:00:00Z' }] }
      },
      {
        method: 'POST', path: '/api/characters', auth: true,
        desc: 'Crear y publicar un nuevo personaje. Sube los 24 frames del sprite (8 direcciones × 3 poses) y una descripción.',
        headers: { 'Content-Type': 'application/json' },
        body: { name: 'Mi Personaje', description: 'Oficinista creativo', frames: { front_idle: 'https://...', front_pierna_der: 'https://...', back_idle: 'https://...' }, created_by: 1, preview_url: 'https://...' },
        response: { success: true, character: { id: 2, name: 'Mi Personaje', description: 'Oficinista creativo', created_by: 1 } }
      },
      {
        method: 'PUT', path: '/api/characters/:id', auth: true,
        desc: 'Actualizar un personaje existente. Se puede actualizar el nombre, descripción, frames o preview.',
        headers: { 'Content-Type': 'application/json' },
        params: { id: 'ID numérico del personaje' },
        body: { name: 'Nombre actualizado', description: 'Nueva descripción', frames: { front_idle: 'https://...' }, preview_url: 'https://...' },
        response: { success: true, character: { id: 2, name: 'Nombre actualizado' } }
      },
      {
        method: 'DELETE', path: '/api/characters/:id', auth: true,
        desc: 'Eliminar un personaje de la galería pública.',
        params: { id: 'ID numérico del personaje' },
        response: { success: true }
      }
    ]
  },
  {
    id: 'public_links', label: 'Dominios y Enlaces Públicos', icon: '🌐', color: '#14B8A6',
    description: 'Endpoints para la gestión de enlaces públicos compartibles bajo el dominio /s/:uuid. Permite acceso seguro externo (sin sesión) a recursos específicos (formularios, documentos) mediante identificadores únicos.',
    apis: [
      {
        method: 'GET', path: '/api/client_documents/public/:uuid', auth: false,
        desc: 'Obtener la configuración y datos guardados de un enlace público. El UUID debe existir y el candado de privacidad (is_public) debe estar activo en la base de datos.',
        params: { uuid: 'Identificador único del recurso compartido' },
        response: { doc: { id: 1, document_type: 'formulario', documents: [], form_data: { is_public: true, public_uuid: 'uuid...' } } }
      },
      {
        method: 'POST', path: '/api/client_documents/public/:uuid', auth: false,
        desc: 'Actualizar los datos de un enlace público por un usuario externo. Permite guardar inputs y archivos de forma anónima en el documento especificado.',
        params: { uuid: 'Identificador único del recurso compartido' },
        body: { documents: [{ name: 'file.pdf', url: '...' }], form_data: { campo: 'valor' } },
        response: { doc: { id: 1, document_type: 'formulario' } }
      }
    ]
  },

];

const METHOD_COLORS = {
  GET: '#10B981', POST: '#3B82F6', PUT: '#F59E0B',
  PATCH: '#8B5CF6', DELETE: '#EF4444', WS: '#EC4899', EVENT: '#6366F1'
};

const curlExample = (api) => {
  if (api.method === 'EVENT' || api.method === 'WS')
    return '// Usar cliente Socket.IO\nconst socket = io("https://colonny.xyz");';
  // La sesión viaja en la cookie httpOnly `colonny-access-token` que /api/login
  // devuelve — con curl, guardarla con -c y reenviarla con -b en requests autenticados.
  const h = api.auth ? `-b cookies.txt \\\n  ` : '';
  const b = api.body ? `-H "Content-Type: application/json" \\\n  -d '${JSON.stringify(api.body, null, 2)}' \\\n  ` : '';
  return `curl -X ${api.method} \\\n  ${h}${b}"${BASE}${api.path}"`;
};

export default function ApiDocs() {
  const [activeSection, setActiveSection] = useState('auth');
  const [openApis, setOpenApis] = useState({});
  const [openSections, setOpenSections] = useState(() => Object.fromEntries(SECTIONS.map(s => [s.id, true])));
  const [allExpanded, setAllExpanded] = useState(true);
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState(null);
  const [activeTabs, setActiveTabs] = useState({});
  const searchRef = useRef(null);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  const toggleAll = () => {
    const next = !allExpanded;
    setAllExpanded(next);
    setOpenSections(Object.fromEntries(SECTIONS.map(s => [s.id, next])));
  };

  const toggleSection = (id) => {
    setOpenSections(p => ({ ...p, [id]: !p[id] }));
  };

  const toggleApi = (key) => {
    setOpenApis(p => ({ ...p, [key]: !p[key] }));
  };

  // Search filter
  const q = search.toLowerCase().trim();
  const filteredSections = useMemo(() => {
    if (!q) return SECTIONS;
    return SECTIONS.map(s => {
      const sectionMatch = s.label.toLowerCase().includes(q) || s.description.toLowerCase().includes(q);
      const matchedApis = s.apis.filter(a =>
        a.path.toLowerCase().includes(q) ||
        a.desc.toLowerCase().includes(q) ||
        a.method.toLowerCase().includes(q)
      );
      if (sectionMatch) return s;
      if (matchedApis.length > 0) return { ...s, apis: matchedApis };
      return null;
    }).filter(Boolean);
  }, [q]);

  const section = SECTIONS.find(s => s.id === activeSection);


  const getResponseTabs = (api) => {
    const tabs = [{ code: 200, label: 'OK', body: api.response }];
    if (api.body) tabs.push({ code: 400, label: 'Bad Request', body: { success: false, error: 'Parámetros inválidos o faltantes' } });
    if (api.auth) tabs.push({ code: 401, label: 'No autorizado', body: { success: false, error: 'No autenticado' } });
    if (api.path && api.path.includes(':id')) tabs.push({ code: 404, label: 'No encontrado', body: { success: false, error: 'Recurso no encontrado' } });
    tabs.push({ code: 500, label: 'Error del servidor', body: { success: false, error: 'Internal server error' } });
    return tabs;
  };
  const TC = (code) => code < 300 ? '#10B981' : code < 500 ? '#F59E0B' : '#EF4444';

  return (
    <div className="apidocs-root">
      {/* Sidebar */}
      <aside className="apidocs-sidebar">
        <div className="apidocs-logo">
          <span className="apidocs-logo-dot" />
          <span>Colonny <span className="apidocs-logo-api">API</span></span>
        </div>
        <p className="apidocs-sidebar-subtitle">Documentación de la plataforma</p>

        {/* Controls row */}
        <div className="apidocs-controls">
          <button
            className="apidocs-toggle-all"
            onClick={toggleAll}
            title={allExpanded ? 'Comprimir todo' : 'Expandir todo'}
          >
            {allExpanded ? '−' : '+'}
          </button>
          <div className="apidocs-search-wrap">
            <input
              ref={searchRef}
              className="apidocs-search"
              type="text"
              placeholder="Buscar API..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className="apidocs-search-clear" onClick={() => setSearch('')}>×</button>
            )}
          </div>
        </div>

        {/* Nav tree */}
        <nav className="apidocs-nav">
          {filteredSections.map(s => (
            <div key={s.id} className="apidocs-nav-group">
              {/* Section header */}
              <button
                className={`apidocs-nav-item ${activeSection === s.id ? 'active' : ''}`}
                style={activeSection === s.id ? { borderLeftColor: s.color, color: s.color } : {}}
                onClick={() => { setActiveSection(s.id); }}
              >
                <span className="apidocs-nav-icon">{s.icon}</span>
                <span className="apidocs-nav-label">{s.label}</span>
                <button
                  className="apidocs-nav-chevron"
                  onClick={e => { e.stopPropagation(); toggleSection(s.id); }}
                >
                  {openSections[s.id] ? '▾' : '▸'}
                </button>
              </button>

              {/* API sub-items */}
              {openSections[s.id] && (
                <div className="apidocs-nav-sub">
                  {s.apis.map((api, i) => (
                    <button
                      key={i}
                      className="apidocs-nav-sub-item"
                      onClick={() => {
                        setActiveSection(s.id);
                        const key = `${s.id}-${i}`;
                        setOpenApis(p => ({ ...p, [key]: true }));
                        setTimeout(() => {
                          document.getElementById(`api-card-${s.id}-${i}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 100);
                      }}
                    >
                      <span
                        className="apidocs-nav-badge"
                        style={{ background: METHOD_COLORS[api.method] || '#6B7280' }}
                      >
                        {api.method}
                      </span>
                      <span className="apidocs-nav-sub-path">{api.path.split('?')[0]}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="apidocs-sidebar-footer">
          <span>Base URL</span>
          <code>{BASE}</code>
        </div>
      </aside>

      {/* Main */}
      <main className="apidocs-main">
        {filteredSections.length === 0 ? (
          <div className="apidocs-empty">
            <span>🔍</span>
            <p>No se encontraron resultados para <strong>"{search}"</strong></p>
          </div>
        ) : (
          filteredSections
            .filter(s => !q || s.id === (filteredSections[0]?.id))
            .map(s => s.id === activeSection ? s : null)
            .filter(Boolean)
            .concat(q ? filteredSections : [])
            .filter((s, i, arr) => arr.findIndex(x => x?.id === s?.id) === i)
            .filter(Boolean)
            .map(s => (
              <div key={s.id}>
                {(q || s.id === activeSection) && (
                  <>
                    <div className="apidocs-section-header" style={{ borderColor: s.color }}>
                      <span className="apidocs-section-icon">{s.icon}</span>
                      <div>
                        <h1 className="apidocs-section-title">{s.label}</h1>
                        <p className="apidocs-section-desc">{s.description}</p>
                      </div>
                    </div>
                    {s.apis.map((api, i) => {
                      const key = `${s.id}-${i}`;
                      const isOpen = openApis[key];
                      return (
                        <div key={key} id={`api-card-${s.id}-${i}`} className={`apidocs-card ${isOpen ? 'open' : ''}`}>
                          <button className="apidocs-card-header" onClick={() => toggleApi(key)}>
                            <div className="apidocs-card-left">
                              <span className="apidocs-badge" style={{ background: METHOD_COLORS[api.method] || '#6B7280' }}>
                                {api.method}
                              </span>
                              <code className="apidocs-path">{api.path}</code>
                            </div>
                            <div className="apidocs-card-right">
                              {api.auth && <span className="apidocs-auth-tag">🔒 auth</span>}
                              <span className="apidocs-chevron">{isOpen ? '▲' : '▼'}</span>
                            </div>
                          </button>
                          <p className="apidocs-card-desc">{api.desc}</p>
                          {isOpen && (
                            <div className="apidocs-card-body">
                              {api.headers && (
                                <div className="apidocs-block">
                                  <div className="apidocs-block-title">Headers</div>
                                  <pre className="apidocs-code">{JSON.stringify(api.headers, null, 2)}</pre>
                                </div>
                              )}
                              {api.params && (
                                <div className="apidocs-block">
                                  <div className="apidocs-block-title">Parámetros URL</div>
                                  <pre className="apidocs-code">{JSON.stringify(api.params, null, 2)}</pre>
                                </div>
                              )}
                              {api.body && (
                                <div className="apidocs-block">
                                  <div className="apidocs-block-title">Body (JSON)</div>
                                  <pre className="apidocs-code">{JSON.stringify(api.body, null, 2)}</pre>
                                </div>
                              )}
                              <div className="apidocs-block">
                                <div className="apidocs-block-title">Respuesta</div>
                                <div className="apidocs-resp-tabs">
                                  {getResponseTabs(api).map(t => (
                                    <button key={t.code}
                                      className={`apidocs-resp-tab ${(activeTabs[key]??200)===t.code?'active':''}`}
                                      style={{'--tc': TC(t.code)}}
                                      onClick={()=>setActiveTabs(p=>({...p,[key]:t.code}))}>
                                      <span className="apidocs-resp-dot"/>
                                      {t.code} {t.label}
                                    </button>
                                  ))}
                                </div>
                                <pre className="apidocs-code">
                                  {JSON.stringify(getResponseTabs(api).find(t=>t.code===(activeTabs[key]??200))?.body,null,2)}
                                </pre>
                              </div>
                              <div className="apidocs-block">
                                <div className="apidocs-block-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span>Ejemplo cURL</span>
                                  <button className="apidocs-copy-btn" onClick={() => copy(curlExample(api), key)}>
                                    {copied === key ? '✓ Copiado' : '⎘ Copiar'}
                                  </button>
                                </div>
                                <pre className="apidocs-code apidocs-curl">{curlExample(api)}</pre>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            ))
        )}
      </main>
    </div>
  );
}
